/**
 * What this company has cost so far (R6).
 *
 * In the card's footer rather than behind a tab, and small. The point is not to
 * make anybody study it; it is that the two expensive buttons at the top of this
 * card should never be pressed by somebody who has no idea what the last press
 * cost. A number nobody has to go looking for does that; a tab does not.
 *
 * TWO FIGURES, NOT ONE TOTAL. Model tokens are money against the provider
 * account. Browser milliseconds are an allowance - ten minutes a day for the
 * whole platform, shared with the template thumbnails - and no exchange rate
 * between them exists. Adding them would be arithmetic on unlike units and the
 * resulting number would be wrong in a way nobody could see.
 */
import { Coins } from "lucide-react";
import type { BrainCost } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

export function BrainCostLine({ cost, t }: { cost: BrainCost | null; t: T }) {
  // Nothing recorded is no line at all. A row of zeros would be a confident
  // claim about a run that may well have cost money before these columns
  // existed, which is worse than saying nothing.
  if (!cost) return null;

  const parts: string[] = [];

  if (cost.tokensIn || cost.tokensOut) {
    parts.push(t("brain.cost.tokens", { in: fmtTokens(cost.tokensIn), out: fmtTokens(cost.tokensOut) }));
    // "Unknown", never "0.00". A model missing from the price table and a run
    // that was free look nothing alike to somebody deciding whether to press
    // the button again, and only one of them is true.
    parts.push(cost.cents === null ? t("brain.cost.unpriced") : t("brain.cost.money", { eur: fmtEur(cost.cents) }));
  }

  if (cost.browserMs !== null) {
    const seconds = Math.round(cost.browserMs / 100) / 10;
    parts.push(
      // The estimate flag is not decoration. Cloudflare reports the real figure
      // on some responses and not others, and a failed viewport is charged at a
      // flat guess - a daily budget built out of assumptions presented as
      // measurements is how an allowance runs out at noon with the counter
      // still saying there is plenty left.
      cost.browserMsEstimated
        ? t("brain.cost.browserAbout", { seconds })
        : t("brain.cost.browser", { seconds }),
    );
  }

  if (!parts.length) return null;

  return (
    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground" title={cost.model ?? undefined}>
      <Coins className="size-3.5 shrink-0" />
      {parts.join(" · ")}
    </p>
  );
}

/** Thousands as "12.3k". A token count is read as a magnitude, never exactly. */
function fmtTokens(n: number): string {
  if (n < 1000) return String(n);
  return `${Math.round(n / 100) / 10}k`;
}

/**
 * Cents to euros, with enough places to be non-zero.
 *
 * A brain run costs single-digit cents, so two decimal places would render most
 * of them as "0.00" - which reads as free and is the one impression this line
 * exists to prevent. Four places below ten cents, two above.
 */
function fmtEur(cents: number): string {
  const eur = cents / 100;
  return eur < 0.1 ? eur.toFixed(4) : eur.toFixed(2);
}
